import { useCallback, useState } from "react";
import _ from "lodash";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { AccordionDetails } from "@mui/material";

import api from "services/api";

import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";
import formatResourceLabel from "common/format-resource-label";
import formatBooleanToString from "common/format-boolean-to-string";

import CONSTANTS from "constants/constant";
import { yesNoOptions } from "constants/yes-no-options";

import ActionEnum from "enum/action";

import { RootStateDto } from "dto/redux/store-dto";
import ErrorResponseDto from "dto/services/error-response-dto";
import AppResourceArrayObjectInputDto from "dto/components/pages/resources/app-resource-array-object-input-dto";

import AppInput from "components/app-input";
import AppAccordion from "components/app-accordion";
import AppSelectInput from "components/app-select-input";
import AppResourceArrayWrapper from "./app-resource-array-wrapper";

const hideLabel = ["page.landing.pros"];

const AppResourceArrayObjectInput = ({ detail }: AppResourceArrayObjectInputDto) => {
	const [focusIndex, setFocusIndex] = useState(null);
	const { customerId } = useSelector((state: RootStateDto) => state.profile);
	const keys = detail?.pojo ? Object.keys(detail?.pojo) : [];
	const fieldLabel = hideLabel.includes(detail.key) ? "" : detail?.label || detail.key;

	const initialValues = keys?.reduce((acc: any, key) => {
		acc[key] = "";

		return acc;
	}, {});

	const formik = useFormik<Record<string, any>>({
		initialValues: { data: detail.value || [initialValues] || [] },
		onSubmit: async (values) => {
			const formatValues = values.data.map((item: any) => {
				const formatItem = Object.keys(item).reduce((acc: any, key) => {
					if (key === "isHeadquarter") {
						acc[key] = item[key] === "true";
					} else {
						acc[key] = item[key];
					}

					return acc;
				}, {});

				return formatItem;
			});

			const payload = {
				id: detail.templateId,
				customerId: customerId,
				template: detail.activeTemplate,
				page: detail.page,
				key: detail.key,
				type: detail.type,
				placeholder: detail.placeholder,
				isArray: detail.isArray,
				value: formatValues,
			};

			try {
				await api.post.resources.update(payload);

				serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);
			} catch (error) {
				const err = error as Error | ErrorResponseDto;

				serveRequestErrors(err);
			}
		},
	});

	const isAddDisabled = detail.key === "page.joinTheTeam" && formik.values.data.length >= 9;

	const onHandleAdd = useCallback(() => {
		const isLastFieldsFulfil = formik.values.data.length > 0 ? !_.every(formik.values.data.slice(-1)[0], _.isEmpty) : true;

		if (isLastFieldsFulfil && !isAddDisabled) {
			formik.setFieldValue("data", [...formik.values.data, initialValues]);

			setFocusIndex(formik.values.data.length);
		}
	}, [formik, initialValues, isAddDisabled]);

	//prettier-ignore
	const onHandleDelete = useCallback((index: number) => {
        const dataAfterRemoved = formik.values.data.filter((_: any, i: number) => i !== index);

        formik.setFieldValue("data", dataAfterRemoved);
    }, [formik]);

	return (
		<AppResourceArrayWrapper onAdd={onHandleAdd} onUpdate={formik.handleSubmit} title={fieldLabel} disabled={isAddDisabled}>
			<div className="array-wrapper__ao-inputs">
				{formik.values.data.map((item: any, index: number) => (
					<AppAccordion key={`ao-${index}`} title={`Item ${index + 1}`} onDelete={() => onHandleDelete(index)} defaultActiveIndex={focusIndex === index}>
						<AccordionDetails>
							{keys.map((key) => {
								const concatKey = `data.${index}.${key}`;

								if (key === "isHeadquarter") {
									return (
										<>
											{/* prettier-ignore */}
											<AppSelectInput type="text" key={concatKey} name={concatKey} label={formatResourceLabel(key)} placeholder="" value={formatBooleanToString(formik.values.data[index][key])} onChange={formik.handleChange} options={yesNoOptions} />
										</>
									);
								}

								return <AppInput type="text" key={concatKey} name={concatKey} label={formatResourceLabel(key)} placeholder="" value={formik.values.data[index][key]} onChange={formik.handleChange} />;
							})}
						</AccordionDetails>
					</AppAccordion>
				))}
			</div>
		</AppResourceArrayWrapper>
	);
};

export default AppResourceArrayObjectInput;
